import { FC, useCallback, useRef } from 'react';
import * as Icon from 'react-feather';
import { UploadRequestOption } from 'rc-upload/es/interface';
import {
  ApiErrorResponse,
  ClassifierMappingsType,
  Popconfirm,
  useMutateClassifierMappingUpload,
  ProgrammaticUpload,
  ProgrammaticUploadRef,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ClassifierMappingUpload.translations.json';
import { Button } from 'antd';

const { addTranslations, useTranslation } = i18nNsFactory('classifier-upload');
addTranslations(translations);

export interface ClassifierMappingUploadProps {
  onProgress?: (progress: number) => void;
  onError?: (error: ApiErrorResponse) => void;
  onSuccess?: (classifier: Partial<ClassifierMappingsType>) => void;
  showWarning: boolean;
}

export const ClassifierMappingUpload: FC<ClassifierMappingUploadProps> = ({
  onError,
  onProgress,
  onSuccess,
  showWarning,
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useMutateClassifierMappingUpload(onProgress);

  const handleUpload = useCallback(
    (options: UploadRequestOption) => {
      const { file } = options;
      mutate(
        { file },
        {
          onSuccess,
          onError,
        },
      );
    },
    [mutate, onError, onSuccess],
  );

  const uploadMainRef = useRef<ProgrammaticUploadRef | null>(null);
  const handleUploadInit = useCallback(() => {
    uploadMainRef.current?.startUpload();
  }, []);

  return (
    <div>
      {showWarning ? (
        <Popconfirm
          id="confirm-upload-classifierMapping"
          title={t('title')}
          description={t('description')}
          cancelText={t('button.no')}
          okText={t('button.yes')}
          onConfirm={handleUploadInit}
        >
          <UploadButton isLoading={isPending} />
        </Popconfirm>
      ) : (
        <UploadButton onClick={handleUploadInit} isLoading={isPending} />
      )}
      <ProgrammaticUpload
        id="upload-classifierMapping-input"
        customRequest={handleUpload}
        showUploadList={false}
        ref={uploadMainRef}
      />
    </div>
  );
};

const UploadButton: FC<{ onClick?: () => void; isLoading: boolean }> = ({
  onClick,
  isLoading,
}) => (
  <Button
    id="upload-classifierMapping"
    icon={<Icon.Upload />}
    onClick={onClick}
    disabled={isLoading}
  />
);
